import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Heading, Text, HStack, VStack, Button, Table, Thead, Tbody, Tr, Th, Td,
  IconButton, Badge, Spinner, Alert, AlertIcon, useToast,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
  FormControl, FormLabel, FormHelperText, Input, useDisclosure,
} from '@chakra-ui/react';
import { FiEdit2, FiTrash2, FiPlus, FiUsers } from 'react-icons/fi';

const API_BASE = '/api/v1';

const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Token ${localStorage.getItem('assistant_token')}`,
});

const emptyForm = { username: '', email: '', password: '' };

function Users() {
  const toast = useToast();
  const [users, setUsers] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [editingUser, setEditingUser] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  const { isOpen: isUserModalOpen, onOpen: onUserModalOpen, onClose: onUserModalClose } = useDisclosure();
  const { isOpen: isDeleteModalOpen, onOpen: onDeleteModalOpen, onClose: onDeleteModalClose } = useDisclosure();
  const [userToDelete, setUserToDelete] = useState(null);

  const fetchAll = useCallback(() => {
    setLoading(true);
    Promise.all([
      fetch(`${API_BASE}/users/`, { headers: getAuthHeaders() }).then(res => res.json()),
      fetch(`${API_BASE}/users/me/`, { headers: getAuthHeaders() }).then(res => res.json()),
    ])
      .then(([usersData, meData]) => {
        setUsers(Array.isArray(usersData) ? usersData : []);
        setCurrentUser(meData);
        setError(null);
      })
      .catch(() => setError('Could not load users.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openAddUser = () => {
    setEditingUser(null);
    setForm(emptyForm);
    setFormError(null);
    onUserModalOpen();
  };

  const openEditUser = (user) => {
    setEditingUser(user);
    setForm({ username: user.username, email: user.email || '', password: '' });
    setFormError(null);
    onUserModalOpen();
  };

  const handleSave = async () => {
    setSaving(true);
    setFormError(null);
    const isSelf = editingUser && currentUser && editingUser.id === currentUser.id;
    const method = editingUser ? 'PUT' : 'POST';
    const url = editingUser ? `${API_BASE}/users/${editingUser.id}/` : `${API_BASE}/users/`;

    const payload = { username: form.username, email: form.email };
    if (form.password) payload.password = form.password;

    try {
      const res = await fetch(url, { method, headers: getAuthHeaders(), body: JSON.stringify(payload) });
      const data = await res.json();
      if (!res.ok) {
        setFormError(data.error || 'Failed to save user');
        return;
      }
      if (editingUser) {
        setUsers(users.map(u => (u.id === editingUser.id ? data : u)));
        if (isSelf) setCurrentUser(data);
      } else {
        setUsers([...users, data].sort((a, b) => a.username.localeCompare(b.username)));
      }
      toast({
        title: editingUser ? (isSelf ? 'Your account was updated' : 'User updated') : 'User created',
        status: 'success',
        duration: 3000,
      });
      onUserModalClose();
    } catch (e) {
      setFormError('Error saving user');
    } finally {
      setSaving(false);
    }
  };

  const confirmDeleteUser = (user) => {
    setUserToDelete(user);
    onDeleteModalOpen();
  };

  const handleDelete = async () => {
    if (!userToDelete) return;
    try {
      const res = await fetch(`${API_BASE}/users/${userToDelete.id}/`, { method: 'DELETE', headers: getAuthHeaders() });
      const data = await res.json();
      if (res.ok) {
        setUsers(users.filter(u => u.id !== userToDelete.id));
        toast({ title: 'User deleted', status: 'success', duration: 3000 });
      } else {
        toast({ title: data.error || 'Error deleting user', status: 'error', duration: 4000 });
      }
    } catch (e) {
      toast({ title: 'Error deleting user', status: 'error', duration: 4000 });
    }
    onDeleteModalClose();
    setUserToDelete(null);
  };

  if (loading) return <Box textAlign="center" py={20}><Spinner size="xl" color="purple.500" /></Box>;

  return (
    <Box>
      <HStack justify="space-between" mb={6}>
        <VStack align="start" spacing={1}>
          <Heading size="lg">Users</Heading>
          <Text color="gray.500">Who can log into this assistant's admin UI. Edit your own row to change your password.</Text>
        </VStack>
        <Button leftIcon={<FiPlus />} colorScheme="purple" onClick={openAddUser}>Add User</Button>
      </HStack>

      {error && <Alert status="error" mb={4} borderRadius="md"><AlertIcon />{error}</Alert>}

      <Box bg="white" borderRadius="lg" boxShadow="md" overflowX="auto">
        <Table variant="simple">
          <Thead>
            <Tr>
              <Th>Username</Th>
              <Th>Email</Th>
              <Th>Status</Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {users.map((user) => {
              const isSelf = currentUser && user.id === currentUser.id;
              return (
                <Tr key={user.id}>
                  <Td fontWeight="medium">
                    {user.username}
                    {isSelf && <Badge ml={2} colorScheme="purple" fontSize="xs">You</Badge>}
                  </Td>
                  <Td>{user.email || '-'}</Td>
                  <Td>
                    <Badge colorScheme={user.is_active ? 'green' : 'red'}>
                      {user.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </Td>
                  <Td>
                    <HStack spacing={2}>
                      <IconButton
                        icon={<FiEdit2 />}
                        size="sm"
                        colorScheme="blue"
                        variant="ghost"
                        onClick={() => openEditUser(user)}
                        aria-label="Edit user"
                      />
                      <IconButton
                        icon={<FiTrash2 />}
                        size="sm"
                        colorScheme="red"
                        variant="ghost"
                        onClick={() => confirmDeleteUser(user)}
                        aria-label="Delete user"
                        isDisabled={isSelf}
                      />
                    </HStack>
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
        {users.length === 0 && (
          <Box textAlign="center" py={10}>
            <FiUsers size={28} color="gray" />
            <Text color="gray.500" mt={2}>No users found</Text>
          </Box>
        )}
      </Box>

      <Modal isOpen={isUserModalOpen} onClose={onUserModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {editingUser
              ? (currentUser && editingUser.id === currentUser.id ? 'Edit Your Account' : `Edit ${editingUser.username}`)
              : 'Add User'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              {formError && <Alert status="error" borderRadius="md"><AlertIcon />{formError}</Alert>}
              <FormControl isRequired>
                <FormLabel>Username</FormLabel>
                <Input
                  value={form.username}
                  onChange={(e) => setForm({ ...form, username: e.target.value })}
                  placeholder="Enter username"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Email</FormLabel>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="Enter email"
                />
              </FormControl>

              <FormControl isRequired={!editingUser}>
                <FormLabel>{editingUser ? 'New Password' : 'Password'}</FormLabel>
                <Input
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder={editingUser ? 'Leave blank to keep current' : 'Enter password'}
                />
                {editingUser && <FormHelperText>Leave blank to keep the current password.</FormHelperText>}
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onUserModalClose}>Cancel</Button>
            <Button colorScheme="purple" onClick={handleSave} isLoading={saving}>
              {editingUser ? 'Save Changes' : 'Create User'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isDeleteModalOpen} onClose={onDeleteModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Delete User</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            Are you sure you want to delete <strong>{userToDelete?.username}</strong>? This action cannot be undone.
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onDeleteModalClose}>Cancel</Button>
            <Button colorScheme="red" onClick={handleDelete}>Delete</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default Users;
